<script setup lang="ts">
import MiiaFwSignin from '@/modules/miiaFw/components/AuthSignin.vue'
import MiiaFwSignup from '@/modules/miiaFw/components/AuthSignup.vue'
import { useRouter } from 'vue-router'
import MiiaFwConfig from '@/configs/config'
import { computed, useTemplateRef } from 'vue'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { storeToRefs } from 'pinia'
import { ROUTER_PATHS } from '@/constants/router/paths'
import { useGovernmentSettingStore } from '@/stores/governmentSettingStore'
import { hasDeviceAuth, isEnableDisasterUser } from '@/modules/auth/authority'
import { useHoldTemporaryStore } from '@/stores/holdTemporaryStore'
import TopMenuButton from '@/components/common/button/TopMenuButton.vue'
import {
  useDeviceAuthValidWatcher,
  useRouteHomeWatcher
} from '@/composables/deviceAuth/useDeviceAuth'
import { useRouterUtils } from '@/router/useRouterUtils'

const signin = useTemplateRef<InstanceType<typeof MiiaFwSignin>>('signin')
const signup = useTemplateRef<InstanceType<typeof MiiaFwSignup>>('signup')

const miiaFwStore = useMiiaFwStore()
const { isAuthorized } = storeToRefs(miiaFwStore)
const governmentSettingStore = useGovernmentSettingStore()
const holdTemporaryStore = useHoldTemporaryStore()

const router = useRouter()

const govSettings = governmentSettingStore.getGovernmentSetting

const deviceInfo = computed(() => holdTemporaryStore.deviceInfo)

/**
 * @description 未認証状態かどうかを返却する
 * @param { } - 引数なし
 * @return { boolean }  未認証の場合にtrue, 認証済みであればfalse
 */
const unauthorized = computed((): boolean => {
  return MiiaFwConfig.config.authorization.cognitoEnable && !isAuthorized.value
})

/**
 * @description デバイス認証が必要かどうかを返却する
 * @param { } - 引数なし
 * @return { boolean }  登録必要な場合にtrue, 登録不要あればfalse
 */
const requiresAuthentication = computed((): boolean => {
  return !hasDeviceAuth() && miiaFwStore.isPermitted && isEnableDisasterUser()
})

/**
 * @description デバイス登録が不要かどうかを返却する
 * @param { } - 引数なし
 * @return { boolean }  登録不要な場合にtrue, 登録必要であればfalse
 */
const isDeviceAuthNotRequired = computed(() => {
  return hasDeviceAuth() && miiaFwStore.isPermitted && isEnableDisasterUser()
})

/**
 * @callback openSignin
 * @description サインイン用のモーダル画面を開く。
 * @param { } - 引数なし
 * @return { } 戻り値なし
 */
const openSignin = (): void => {
  signin.value?.openModal()
}

/**
 * @callback openSignup
 * @description サインアップ用のモーダル画面を開く。
 * @param { } - 引数なし
 * @return { } 戻り値なし
 */
const openSignup = (): void => {
  signup.value?.openModal()
}

const moveToDeviceAuth = () => {
  const { transition } = useRouterUtils(router)
  transition(ROUTER_PATHS.deviceAuth)
}

/**
 * ログイン画面でデバイス登録ボタンを表示するか切り替える
 * @param {boolean} requiresAuthentication デバイス登録が必要かどうか
 * @return {Promise<void>} 戻り値なし
 */
useRouteHomeWatcher(requiresAuthentication)

// デバイス解除フローがあった場合を監視する
useDeviceAuthValidWatcher(deviceInfo)
</script>

<template>
  <div class="login">
    <MiiaFwSignin ref="signin"></MiiaFwSignin>
    <MiiaFwSignup ref="signup"></MiiaFwSignup>
    <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12 main-area">
      <div class="sysName">
        <span class="display-3">{{ MiiaFwConfig.config.sysName }}</span>
      </div>
      <div v-if="unauthorized" class="primary-area">
        <p v-if="!miiaFwStore.isPermitted" class="text-area">サインインしてご利用ください。</p>
        <button v-if="unauthorized" @click="openSignin" class="btn btn-primary ff-btn-primary">
          <a>&nbsp;サインイン&nbsp;</a>
        </button>
      </div>
      <hr class="my-4" />
      <div
        class="secondary-area"
        v-if="MiiaFwConfig.config.authorization.userSignupEnable && unauthorized"
      >
        <p v-if="!miiaFwStore.isPermitted" class="text-area">
          ユーザーIDをお持ちでない方は以下よりユーザー登録をお願いします。
        </p>
        <button v-if="!isAuthorized" @click="openSignup" class="btn btn-secondary ff-btn-secondary">
          <a>ユーザー登録</a>
        </button>
      </div>
      <div
        v-if="isDeviceAuthNotRequired"
        class="device-info"
        v-show="govSettings.isDeviceAuthentication"
      >
        <div class="container top-menu-btn-container">
          <div class="row border-bottom mb-2">
            <div class="col p-3 ml-3">
              <font-awesome-icon icon="info" class="mr-2" />デバイス情報
            </div>
          </div>
          <div class="p-3 description">
            <p>
              デバイスID: {{ deviceInfo.deviceId }}<br />
              デバイス名称: {{ deviceInfo.deviceName }}<br />
              アクセス許可: {{ deviceInfo.permissionStatus === 'allow' ? '許可' : '禁止' }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="requiresAuthentication">
        <TopMenuButton
          v-if="requiresAuthentication"
          class="mx-2"
          title="デバイス登録"
          iconName="bookmark"
          description="被害調査統合システムで発行した2次元コードを読み取り、本アプリを利用する端末として登録をおこないます。"
          @click="moveToDeviceAuth"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.sysName {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 10vh;
  font-size: 5vw;
}

.top-menu-btn {
  background-color: white;
  border: 1px solid #c1c1c1;
}

.main-area {
  padding-top: 50px;
  padding-bottom: 20px;
  margin-top: 50px;
  border: solid 1px #dcdcdc;
  text-align: center;
  width: 85%;
  margin-left: auto;
  margin-right: auto;
}

.primary-area {
  margin-right: 30px;
  margin-left: 30px;
  margin-top: 50px;
  margin-bottom: 60px;
}

.secondary-area {
  margin: 30px;
}

.text-area {
  font-size: 1.1em;
}

.btn-primary {
  background-color: #01916d;
  border: solid 1px #01916d;
  padding-left: 160px;
  padding-right: 160px;
  padding-top: 20px;
  padding-bottom: 20px;
  border-radius: 3px;
  font-size: 20px;
}

.btn-secondary {
  background-color: #ffffff;
  color: #333333;
  border: solid 1px #333333;
  padding-left: 160px;
  padding-right: 160px;
  padding-top: 20px;
  padding-bottom: 20px;
  font-size: 20px;
}

.device-info {
  text-align: left;
  margin: 40px;
  border: solid 1px #dcdcdc;
}
</style>
